<script setup lang="ts">
import { watch } from 'vue';
import { trackInsightEvent } from '@/api/tracking';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import { useInsightSaveToggle } from '@/components/insights/use-insight-save-toggle';
import type { InsightHintTrackingContext } from '@/types/insights';
import { useLayoutVariant } from '@/utils/composables/useLayoutVariant';

interface Props {
  hintUuid: string;
  initialSaved: boolean;
  tracking: InsightHintTrackingContext;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  settled: [saved: boolean];
}>();

const layoutVariant = useLayoutVariant();

const trackSavedState = (saved: boolean) => {
  const { pageName, insightId, axisId, axisName, hintId } = props.tracking;
  void trackInsightEvent(
    saved ? 'insight_saved_hint_add' : 'insight_saved_hint_remove',
    {
      page: { name: pageName },
      insight: { id: insightId },
      axis: { id: axisId, name: axisName },
      hint: { id: hintId },
      layout_variant: layoutVariant,
    },
  );
};

const { saved, settledSaved, toggle } = useInsightSaveToggle(
  props.hintUuid,
  props.initialSaved,
  trackSavedState,
);

watch(settledSaved, value => emit('settled', value));
</script>

<template>
  <button
    type="button"
    class="c-btn c-btn--auto c-btn--small c-outlineBtn--secondary"
    :class="{ saved }"
    :aria-label="saved ? '保存を解除' : '保存する'"
    @click="toggle"
    v-tooltip="{ content: saved ? '保存を解除' : '保存する' }"
  >
    <DgrIcon
      v-if="saved"
      name="bookmark-fill"
      size="small"
      :keep-fill="false"
    />
    <DgrIcon v-else name="bookmark" size="small" />
  </button>
</template>

<style lang="scss" scoped>
button {
  border: none;
  padding: 8px;

  &:not(:hover) {
    background: #fff;
  }

  &.saved {
    color: $color-green600;
  }
}
</style>
